import { DateTime } from "luxon"
import { getPaymentAmount, isPlaceholderPartecipant } from "./func.utils"
import {
	BILLED_TO_SCHOOL,
	getCourtShare,
	getLessonBilledTo,
	getLessonHours,
	getPlayerPrice,
	isCourtPaidByPlayers,
	isSubscriptionCourse,
	SUBSTITUTE_PAID_TO_SCHOOL,
} from "./billing.utils"

// ──────────────────────────────────────────────────────────────────────────
// CORSI IN ABBONAMENTO
//
// Al SAT e al CAD si viene con l'abbonamento: è il modo di pagare che ci si
// aspetta, e l'allievo nasce così. Chi invece paga la volta — qualcuno che
// prova, un sostituto — paga quello che pagherebbe in una privata con lo
// stesso numero di allievi: il listino dei corsi non è un prezzo a lezione.
//
// Quali sono i corsi lo sanno i conti (`billing.utils`): chi viene al posto
// di un abbonato non paga il maestro come gli altri allievi.
// ──────────────────────────────────────────────────────────────────────────
export { SUBSCRIPTION_COURSE_TYPES, isSubscriptionCourse } from "./billing.utils"

// Chi incassa chi viene la volta al posto di un abbonato: lo decide il
// circolo per ogni corso, e dove non l'ha detto è il circolo — com'è al
// Siro. Fuori dai corsi non c'è niente da decidere: incassa il maestro, come
// per ogni allievo delle sue lezioni.
export const substitutePaidToFor = (lessonTypeID, lessonType) =>
	isSubscriptionCourse(lessonTypeID)
		? (lessonType?.substitutePaidTo ?? SUBSTITUTE_PAID_TO_SCHOOL)
		: null

// Il tipo di lezione da cui si prende il prezzo. Per un corso è la privata
// con tanti allievi quanti ne ha la lezione; oltre la più grande che c'è si
// resta su quella, perché un gruppo da sei paga come uno da quattro.
export const priceLessonTypeFor = (lessonTypeID, playersCount, lessonTypes = {}) => {
	if (!isSubscriptionCourse(lessonTypeID)) return lessonTypeID

	const sizes = Object.keys(lessonTypes)
		.map(id => /^pri(\d+)$/.exec(id))
		.filter(Boolean)
		.map(match => Number(match[1]))
		.sort((a, b) => a - b)
	if (sizes.length === 0) return lessonTypeID

	const wanted = Math.max(1, playersCount || 0)
	const size = sizes.filter(s => s <= wanted).pop() ?? sizes[0]
	return `pri${size}`
}

// ──────────────────────────────────────────────────────────────────────────
// SOSTITUTI
//
// Il sostituto sta tra i partecipanti della lezione — così lo trovano le
// sue lezioni, i suoi pagamenti e il riepilogo — ma con `substituteFor`,
// che dice chi sostituisce. Prende il posto dell'assente: non conta per il
// numero di allievi, quindi non cambia né il tipo né il prezzo della
// lezione.
// ──────────────────────────────────────────────────────────────────────────

// Come un sostituto riconosce chi sostituisce: l'ID, o il nome per chi un
// ID non ce l'ha.
export const substituteKey = player => player?.playerID || player?.playerName || ""

export const isSubstitute = player => Boolean(player?.substituteFor)

export const regularPartecipants = partecipants =>
	(partecipants || []).filter(player => !isSubstitute(player))

export const lessonSubstitutes = partecipants => (partecipants || []).filter(isSubstitute)

export const substituteOf = (partecipants, absent) => {
	const key = substituteKey(absent)
	if (!key) return null
	return (partecipants || []).find(player => player?.substituteFor === key) ?? null
}

// I sostituti che hanno ancora qualcuno da sostituire: chi è uscito dalla
// lezione, o è tornato presente, si porta via anche il suo.
export const keptSubstitutes = (substitutes, regulars) =>
	(substitutes || []).filter(substitute =>
		(regulars || []).some(
			player =>
				player.isPresent === false && substituteKey(player) === substitute.substituteFor,
		),
	)

// I partecipanti con il sostituto di `absent` messo, cambiato o tolto
// (`substitute` null). Uno solo per assente: un secondo prende il posto del
// primo.
export const withSubstitute = (partecipants, absent, substitute) => {
	const key = substituteKey(absent)
	const others = (partecipants || []).filter(player => player?.substituteFor !== key)
	if (!substitute) return others
	return [...others, { ...substitute, isPresent: true, substituteFor: key }]
}

// ──────────────────────────────────────────────────────────────────────────
// RECUPERI
//
// Un'assenza di chi ha l'abbonamento è una lezione da recuperare, e ogni
// lezione pagata con "Recupero" ne usa una. Si contano nella stagione, che
// comincia a settembre: quelle dell'anno prima non si portano dietro.
//
// E si contano per corso: un CAD saltato si recupera in un altro CAD, un SAT
// in un altro SAT. Sono corsi diversi, con allievi diversi: un posto in un
// SAT non si riempie con chi viene al CAD. Fuori dai corsi non si recupera.
// ──────────────────────────────────────────────────────────────────────────
export const SEASON_START_MONTH = 9

export const getSeasonBounds = date => {
	const day = DateTime.fromJSDate(date)
	const startYear = day.month >= SEASON_START_MONTH ? day.year : day.year - 1
	const start = DateTime.fromObject({ year: startYear, month: SEASON_START_MONTH, day: 1 })
	return { start: start.toJSDate(), end: start.plus({ years: 1 }).toJSDate() }
}

// "Stagione 2026/27", per i titoli dei conti della stagione
export const seasonLabel = season =>
	`Stagione ${season.start.getFullYear()}/${String(season.end.getFullYear()).slice(-2)}`

// Quanti recuperi del corso `courseType` restano all'allievo, date le sue
// lezioni della stagione. La lezione che si sta scrivendo non conta: un
// recupero che ha già lì è quello che si sta guardando, non uno in meno.
// Un'assenza da abbonato: la lezione persa che va recuperata
export const isSubscriptionAbsence = player =>
	player?.isPresent === false && player.paymentMethod === "subscription"

// Un'assenza recuperata fuori dall'app, di solito con un altro maestro del
// circolo: resta scritta, ma non è più da recuperare. Il segno vale solo
// sull'assenza: su chi è presente non vuol dire niente.
export const isRecoveredElsewhere = player =>
	isSubscriptionAbsence(player) && player.recoveredElsewhere === true

// I partecipanti senza i segni rimasti a chi un'assenza non ce l'ha più: chi
// torna presente, o non paga più con l'abbonamento, se lo perde. Se no,
// segnato di nuovo assente, si ritroverebbe un'assenza già recuperata.
export const withoutStaleRecoveredElsewhere = partecipants =>
	(partecipants || []).map(player => {
		if (!player || !("recoveredElsewhere" in player) || isRecoveredElsewhere(player))
			return player
		const { recoveredElsewhere, ...rest } = player
		return rest
	})

// I partecipanti con l'assenza di quello al posto `index` (o con quell'ID,
// se nel frattempo il posto è cambiato) segnata recuperata altrove, o no.
// `null` se non c'è, o se non è un'assenza da abbonato.
export const setRecoveredElsewhere = (partecipants, { index, playerID }, recovered) => {
	const list = partecipants || []
	const atIndex = list[index]
	const target =
		atIndex && (atIndex.playerID ?? "") === (playerID ?? "")
			? index
			: playerID
				? list.findIndex(player => player?.playerID === playerID && !isSubstitute(player))
				: -1
	if (target === -1 || !isSubscriptionAbsence(list[target])) return null

	return list.map((player, i) => {
		if (i !== target) return player
		if (recovered) return { ...player, recoveredElsewhere: true }
		const { recoveredElsewhere, ...rest } = player
		return rest
	})
}

export const recoveriesLeft = (lessons, playerID, excludeLessonID, courseType) => {
	if (!isSubscriptionCourse(courseType)) return 0
	let absences = 0
	let used = 0

	;(lessons || []).forEach(lesson => {
		if (excludeLessonID && lesson.lessonID === excludeLessonID) return
		if (lesson.lessonType !== courseType) return
		;(lesson.partecipants || []).forEach(player => {
			if (player?.playerID !== playerID) return
			if (isSubscriptionAbsence(player)) absences += 1
			// recuperata altrove: come un recupero fatto, ma non qui
			if (isRecoveredElsewhere(player)) used += 1
			if (player.paymentMethod === "recovery") used += 1
		})
	})

	return Math.max(0, absences - used)
}

// Quanto paga la volta un sostituto in questa lezione: il prezzo della
// privata con gli allievi che ha la lezione — un CAD da quattro paga come una
// pri4 — meno la sua quota di campo, se il campo lo pagano gli allievi.
//
// Vale anche nelle lezioni fatturate al circolo: lì gli allievi non pagano
// niente perché hanno l'abbonamento, ma chi viene la volta paga lo stesso.
export const getSubstitutePrice = (lesson, { lessonPrices, courtsFee, rates, lessonTypes }) => {
	const players = regularPartecipants(lesson.partecipants).length
	const hours = getLessonHours(lesson)
	const fullPrice = getPaymentAmount(
		lessonPrices,
		priceLessonTypeFor(lesson.lessonType, players, lessonTypes),
		lesson.sportID,
		hours,
	)

	const court = lesson.court
	const hasCourt = Number(court?.courtNumber ?? court ?? 0) !== 0
	const courtShare =
		isCourtPaidByPlayers(lesson.courtPaidBy) &&
		hasCourt &&
		getLessonBilledTo(lesson, rates) !== BILLED_TO_SCHOOL
			? getCourtShare(courtsFee, {
					sportID: lesson.sportID,
					usingLights: lesson.usingLights,
					usingHeat: lesson.usingHeat,
					date: lesson.lessonStart,
					hours,
					playersCount: players,
				})
			: 0

	return getPlayerPrice(fullPrice, courtShare)
}

// Le assenze da abbonato del periodo, per allievo, con i recuperi fatti nello
// stesso periodo e le lezioni di ognuno: il riepilogo ne dice quante sono e
// toccandole le mostra. Le assenze recuperate altrove stanno tra le assenze,
// perché lo sono, e anche in `elsewhere`, che le toglie dal conto.
export const accumulateAbsences = (acc, lesson) => {
	;(lesson?.partecipants || []).forEach(player => {
		const isAbsence = isSubscriptionAbsence(player)
		const isRecovery = player?.paymentMethod === "recovery"
		if (!isAbsence && !isRecovery) return

		const key = player.playerID || player.playerName || "unknownPlayer"
		const entry = acc[key] ?? {
			playerID: player.playerID ?? "",
			playerName: player.playerName ?? "Non specificato",
			absences: [],
			recoveries: [],
			elsewhere: [],
		}
		;(isAbsence ? entry.absences : entry.recoveries).push(lesson)
		if (isRecoveredElsewhere(player)) entry.elsewhere.push(lesson)
		acc[key] = entry
	})
}

// Le assenze di `accumulateAbsences` divise per corso, `{ cad: {...}, sat:
// {...} }`: ogni corso ha le sue, e i suoi recuperi. Le lezioni che non sono
// corsi non ci entrano, perché lì non si recupera.
export const absencesByCourse = lessons => {
	const byCourse = {}
	;(lessons || []).forEach(lesson => {
		if (!isSubscriptionCourse(lesson?.lessonType)) return
		byCourse[lesson.lessonType] ??= {}
		accumulateAbsences(byCourse[lesson.lessonType], lesson)
	})
	return byCourse
}

// Le assenze e i recuperi di un allievo solo, per corso: `{ cad: entry }`,
// con l'entry di `accumulateAbsences`. Le lezioni che contano per i recuperi
// hanno dentro anche gli altri allievi, che qui non contano.
export const playerAbsencesByCourse = (lessons, playerID) =>
	Object.fromEntries(
		Object.entries(absencesByCourse(lessons))
			.map(([course, entries]) => [course, entries[playerID]])
			.filter(([, entry]) => entry),
	)

// I posti da riempire tra `from` e `to`: in ogni lezione, ogni abbonato
// assente che non ha ancora un sostituto, `{ lesson, absent }`, dal più
// vicino. Sono le stesse lezioni che contano per i recuperi: un posto libero
// è un'assenza da abbonato, e solo sotto gli abbonati si aggiunge un
// sostituto. Una lezione annullata non ha posti: non si fa.
export const openSpots = (lessons, from, to) =>
	(lessons || [])
		.filter(lesson => {
			if (lesson?.cancelled === true) return false
			const start = lesson?.lessonStart?.toDate?.()
			return start && start >= from && start < to
		})
		.flatMap(lesson =>
			regularPartecipants(lesson.partecipants)
				.filter(
					player =>
						player.isPresent === false &&
						player.paymentMethod === "subscription" &&
						!isPlaceholderPartecipant(player) &&
						!substituteOf(lesson.partecipants, player),
				)
				.map(absent => ({ lesson, absent })),
		)
		.sort((a, b) => a.lesson.lessonStart.toDate() - b.lesson.lessonStart.toDate())

// I posti da riempire divisi per corso, `{ cad: [...], sat: [...] }`, come
// le assenze: un posto del CAD si riempie con chi ha un recupero del CAD.
export const openSpotsByCourse = (lessons, from, to) => {
	const byCourse = {}
	openSpots(lessons, from, to).forEach(spot => {
		const course = spot.lesson.lessonType
		if (!isSubscriptionCourse(course)) return
		;(byCourse[course] ??= []).push(spot)
	})
	return byCourse
}

// Quante assenze restano da recuperare, tra quelle del periodo: meno i
// recuperi fatti qui, e quelle recuperate altrove
export const absencesToRecover = entry =>
	Math.max(
		0,
		(entry?.absences?.length ?? 0) -
			(entry?.recoveries?.length ?? 0) -
			(entry?.elsewhere?.length ?? 0),
	)

// Se in questa lezione c'è qualcosa che conta per i recuperi: un'assenza da
// abbonato, o qualcuno che recupera. La lezione se lo scrive in
// `tracksRecovery` ogni volta che si salva, così le assenze della stagione si
// trovano con una query sola, senza scaricare tutte le lezioni.
export const lessonTracksRecovery = partecipants =>
	(partecipants || []).some(
		player =>
			isSubscriptionAbsence(player) || player?.paymentMethod === "recovery",
	)
